/**
 * Mock data for the admin-portal UI shell (ROADS-476 Phase 1 wide-static
 * layer). All four users are real staging signups as of 2026-05-18; the
 * entitlements, charges, and audit events are realistic-looking fixtures
 * matching the schema in V202605050001__entitlements.sql. Wire to the real
 * `GET /api/admin/users` endpoint (ROADS-476 deliverable A1) to swap.
 */

export type AdminUser = {
  id: string;
  initials: string;
  avatarHex: string;
  name: string;
  email: string;
  persona: "immigrant" | "resident" | "cdl";
  language: string;
  homeCountry: string;
  homeCountryEmoji: string;
  targetStates: string[];
  focusState: string | null;
  cdlInterest: boolean;
  signupAt: string;
  lastSeen: string;
  lastSeenDevice: string;
  trialDaysLeft: number;
  trialExpiresAt: string;
};

export const ADMIN_USERS: AdminUser[] = [
  {
    id: "user_01KK4FXF8WVQ5P4KGJTMVBT44X",
    initials: "BC",
    avatarHex: "#283593",
    name: "Bharat Chitnavis",
    email: "bharat@create.cx",
    persona: "immigrant",
    language: "Hindi",
    homeCountry: "India",
    homeCountryEmoji: "\u{1F1EE}\u{1F1F3}",
    targetStates: ["CA"],
    focusState: "CA",
    cdlInterest: false,
    signupAt: "2026-05-17T04:16:18Z",
    lastSeen: "2 min ago",
    lastSeenDevice: "iPhone · Safari",
    trialDaysLeft: 29,
    trialExpiresAt: "2026-06-17T21:16:00Z",
  },
  {
    id: "user_01KRY1AYAS7ARFYJJX84HY56EJ",
    initials: "PM",
    avatarHex: "#3949AB",
    name: "Poojan Mehta",
    email: "poojan@create.cx",
    persona: "cdl",
    language: "English",
    homeCountry: "United States",
    homeCountryEmoji: "\u{1F1FA}\u{1F1F8}",
    targetStates: ["TX"],
    focusState: "TX",
    cdlInterest: true,
    signupAt: "2026-05-17T08:00:00Z",
    lastSeen: "1 h ago",
    lastSeenDevice: "Desktop · Chrome",
    trialDaysLeft: 29,
    trialExpiresAt: "2026-06-17T21:16:00Z",
  },
  {
    id: "user_01KRYAVA1X1GYWP117920D9N3Q",
    initials: "MO",
    avatarHex: "#5C6BC0",
    name: "Maria Ortega",
    email: "maria.o@example.com",
    persona: "resident",
    language: "English",
    homeCountry: "United States",
    homeCountryEmoji: "\u{1F1FA}\u{1F1F8}",
    targetStates: ["TX", "IL", "CA", "NY", "FL"],
    focusState: "TX",
    cdlInterest: true,
    signupAt: "2026-05-18T20:01:37Z",
    lastSeen: "12 min ago",
    lastSeenDevice: "Pixel 8 · Chrome",
    trialDaysLeft: 30,
    trialExpiresAt: "2026-06-17T21:16:00Z",
  },
  {
    id: "user_01KRYAPM51G53VFJZAEJC8C1FG",
    initials: "DR",
    avatarHex: "#7986CB",
    name: "Daniel Reyes",
    email: "daniel.r@example.com",
    persona: "resident",
    language: "English",
    homeCountry: "United States",
    homeCountryEmoji: "\u{1F1FA}\u{1F1F8}",
    targetStates: ["TX", "CA", "IL", "FL"],
    focusState: "TX",
    cdlInterest: true,
    signupAt: "2026-05-18T19:59:17Z",
    lastSeen: "Just now",
    lastSeenDevice: "Pixel 8 · Chrome",
    trialDaysLeft: 30,
    trialExpiresAt: "2026-06-17T21:16:00Z",
  },
];

export type Entitlement = {
  id: string;
  tier: "class_c" | "cdl" | "tourist_in" | "tourist_out" | "motorcycle";
  stateCode: string | null;
  countryCode: string | null;
  source: "trial" | "purchase" | "comp" | "refund_extension";
  expiresAt: string;
  grantedAt: string;
  grantedBy: string;
};

const FAR_FUTURE = "2099-12-31T23:59:59Z";

export const ENTITLEMENTS_BY_USER: Record<string, Entitlement[]> = {
  user_01KK4FXF8WVQ5P4KGJTMVBT44X: [
    {
      id: "ent_001",
      tier: "class_c",
      stateCode: "CA",
      countryCode: null,
      source: "comp",
      expiresAt: FAR_FUTURE,
      grantedAt: "2026-05-18T18:48:50Z",
      grantedBy: "Bharat C.",
    },
    {
      id: "ent_002",
      tier: "class_c",
      stateCode: null,
      countryCode: null,
      source: "trial",
      expiresAt: "2026-06-17T21:16:00Z",
      grantedAt: "2026-05-18T21:16:00Z",
      grantedBy: "system",
    },
  ],
  user_01KRY1AYAS7ARFYJJX84HY56EJ: [
    {
      id: "ent_003",
      tier: "cdl",
      stateCode: "TX",
      countryCode: null,
      source: "comp",
      expiresAt: FAR_FUTURE,
      grantedAt: "2026-05-18T18:48:50Z",
      grantedBy: "Bharat C.",
    },
    {
      id: "ent_004",
      tier: "class_c",
      stateCode: "TX",
      countryCode: null,
      source: "comp",
      expiresAt: FAR_FUTURE,
      grantedAt: "2026-05-18T18:48:50Z",
      grantedBy: "Bharat C.",
    },
    {
      id: "ent_005",
      tier: "cdl",
      stateCode: null,
      countryCode: null,
      source: "trial",
      expiresAt: "2026-06-17T21:16:00Z",
      grantedAt: "2026-05-18T21:16:00Z",
      grantedBy: "system",
    },
    {
      id: "ent_006",
      tier: "class_c",
      stateCode: null,
      countryCode: null,
      source: "trial",
      expiresAt: "2026-06-17T21:16:00Z",
      grantedAt: "2026-05-18T21:16:00Z",
      grantedBy: "system",
    },
  ],
  user_01KRYAVA1X1GYWP117920D9N3Q: [
    ...["TX", "IL", "CA", "NY", "FL"].map((s, i) => ({
      id: `ent_010_${i}`,
      tier: "class_c" as const,
      stateCode: s,
      countryCode: null,
      source: "comp" as const,
      expiresAt: FAR_FUTURE,
      grantedAt: "2026-05-18T18:48:50Z",
      grantedBy: "Bharat C.",
    })),
    {
      id: "ent_015",
      tier: "cdl",
      stateCode: "TX",
      countryCode: null,
      source: "comp",
      expiresAt: FAR_FUTURE,
      grantedAt: "2026-05-18T18:48:50Z",
      grantedBy: "Bharat C.",
    },
    {
      id: "ent_016",
      tier: "tourist_in",
      stateCode: null,
      countryCode: null,
      source: "comp",
      expiresAt: FAR_FUTURE,
      grantedAt: "2026-05-18T18:48:50Z",
      grantedBy: "Bharat C.",
    },
    {
      id: "ent_017",
      tier: "class_c",
      stateCode: null,
      countryCode: null,
      source: "trial",
      expiresAt: "2026-06-17T21:16:00Z",
      grantedAt: "2026-05-18T21:16:00Z",
      grantedBy: "system",
    },
  ],
  user_01KRYAPM51G53VFJZAEJC8C1FG: [
    ...["TX", "CA", "IL", "FL"].map((s, i) => ({
      id: `ent_020_${i}`,
      tier: "class_c" as const,
      stateCode: s,
      countryCode: null,
      source: "comp" as const,
      expiresAt: FAR_FUTURE,
      grantedAt: "2026-05-18T18:48:50Z",
      grantedBy: "Bharat C.",
    })),
    {
      id: "ent_024",
      tier: "cdl",
      stateCode: "TX",
      countryCode: null,
      source: "comp",
      expiresAt: FAR_FUTURE,
      grantedAt: "2026-05-18T18:48:50Z",
      grantedBy: "Bharat C.",
    },
    {
      id: "ent_025",
      tier: "tourist_in",
      stateCode: null,
      countryCode: null,
      source: "comp",
      expiresAt: FAR_FUTURE,
      grantedAt: "2026-05-18T18:48:50Z",
      grantedBy: "Bharat C.",
    },
    {
      id: "ent_026",
      tier: "class_c",
      stateCode: null,
      countryCode: null,
      source: "trial",
      expiresAt: "2026-06-17T21:16:00Z",
      grantedAt: "2026-05-18T21:16:00Z",
      grantedBy: "system",
    },
  ],
};

export type Charge = {
  id: string;
  date: string;
  description: string;
  amountUsd: number;
  method: string;
  status: "paid" | "partial_refund" | "failed" | "credited";
  refundable: boolean;
};

export const CHARGES_BY_USER: Record<string, Charge[]> = {
  user_01KRY1AYAS7ARFYJJX84HY56EJ: [
    {
      id: "ch_3QcdlT1A",
      date: "May 17, 2026",
      description: "CDL prep · Texas · monthly",
      amountUsd: 14.99,
      method: "Visa •••• 4242",
      status: "paid",
      refundable: true,
    },
    {
      id: "ch_3QbXyZ9F",
      date: "Apr 17, 2026",
      description: "CDL prep · Texas · monthly",
      amountUsd: 14.99,
      method: "Visa •••• 4242",
      status: "paid",
      refundable: true,
    },
    {
      id: "cb_1QaA8Y3K",
      date: "Mar 30, 2026",
      description: "Goodwill credit · CS adjustment",
      amountUsd: -4.99,
      method: "Customer balance",
      status: "credited",
      refundable: false,
    },
    {
      id: "ch_3QZpQ2",
      date: "Mar 17, 2026",
      description: "CDL prep · Texas · monthly",
      amountUsd: 14.99,
      method: "Visa •••• 4242",
      status: "partial_refund",
      refundable: true,
    },
    {
      id: "ch_3QY11W",
      date: "Feb 17, 2026",
      description: "CDL prep · Texas · monthly",
      amountUsd: 14.99,
      method: "Visa •••• 4242",
      status: "failed",
      refundable: false,
    },
  ],
};

export type AuditEvent = {
  id: string;
  timestamp: string;
  timestampLabel: string;
  ago: string;
  operatorName: string;
  operatorEmail: string;
  isSystem: boolean;
  action: string;
  actionKind: "ok" | "warn" | "danger" | "info";
  target: string;
  targetSub: string;
  detail: string;
  detailKind: "ok" | "warn" | "danger" | "info";
};

export const AUDIT_EVENTS: AuditEvent[] = [
  {
    id: "ae_001",
    timestamp: "2026-05-18T23:24:00Z",
    timestampLabel: "23:24 UTC",
    ago: "2 min ago",
    operatorName: "Bharat C.",
    operatorEmail: "ops@roadbridge.app",
    isSystem: false,
    action: "secrets.rotate",
    actionKind: "warn",
    target: "POSTGRES_PASSWORD + REDIS_PASSWORD",
    targetSub: "staging-2 · pg_authid + redis CONFIG",
    detail: "in-conversation leak; rotated + accessory-rebooted",
    detailKind: "warn",
  },
  {
    id: "ae_002",
    timestamp: "2026-05-18T23:07:00Z",
    timestampLabel: "23:07 UTC",
    ago: "19 min",
    operatorName: "Bharat C.",
    operatorEmail: "ops@roadbridge.app",
    isSystem: false,
    action: "deploy.staging",
    actionKind: "ok",
    target: "roadbridge-backend-staging",
    targetSub: "SHA 379ead8 · trial-window 30d",
    detail: "44.4s · old container drained · /ready green",
    detailKind: "ok",
  },
  {
    id: "ae_003",
    timestamp: "2026-05-18T21:16:00Z",
    timestampLabel: "21:16 UTC",
    ago: "2 h",
    operatorName: "Bharat C.",
    operatorEmail: "ops@roadbridge.app",
    isSystem: false,
    action: "trial.extend",
    actionKind: "info",
    target: "all 4 staging users",
    targetSub: "Bharat, Poojan, Maria, Daniel",
    detail: "expires_at = NOW() + 30 days · interim while content builds",
    detailKind: "ok",
  },
  {
    id: "ae_004",
    timestamp: "2026-05-18T20:01:37Z",
    timestampLabel: "20:01 UTC",
    ago: "3 h",
    operatorName: "system",
    operatorEmail: "WorkOS webhook",
    isSystem: true,
    action: "user.signup",
    actionKind: "info",
    target: "user_01KRYAVA…D9N3Q",
    targetSub: "Maria Ortega · maria.o@example.com",
    detail: "trial auto-granted (24h, then extended)",
    detailKind: "ok",
  },
  {
    id: "ae_005",
    timestamp: "2026-05-18T19:59:17Z",
    timestampLabel: "19:59 UTC",
    ago: "3 h",
    operatorName: "system",
    operatorEmail: "WorkOS webhook",
    isSystem: true,
    action: "user.signup",
    actionKind: "info",
    target: "user_01KRYAPM…C8C1FG",
    targetSub: "Daniel Reyes · daniel.r@example.com",
    detail: "trial auto-granted (24h, then extended)",
    detailKind: "ok",
  },
  {
    id: "ae_006",
    timestamp: "2026-05-18T18:48:50Z",
    timestampLabel: "18:48 UTC",
    ago: "4 h",
    operatorName: "Bharat C.",
    operatorEmail: "ops@roadbridge.app",
    isSystem: false,
    action: "entitlement.comp_grant",
    actionKind: "info",
    target: "Maria Ortega + Daniel Reyes",
    targetSub: "13 rows · class_c × {TX,IL,CA,NY,FL} + cdl/TX + tourist_in",
    detail: "expires_at = 2099-12-31 · reason: staging testers",
    detailKind: "ok",
  },
  {
    id: "ae_007",
    timestamp: "2026-05-18T16:34:00Z",
    timestampLabel: "16:34 UTC",
    ago: "7 h",
    operatorName: "Bharat C.",
    operatorEmail: "ops@roadbridge.app",
    isSystem: false,
    action: "paddle.drop",
    actionKind: "danger",
    target: "BillingProvider enum + 14 files",
    targetSub: "PaddleCheckoutService, Paddle*, deploy.yml env",
    detail: "merchant rejected by Paddle's banking partners 2026-05-18",
    detailKind: "danger",
  },
  {
    id: "ae_008",
    timestamp: "2026-05-17T21:02:00Z",
    timestampLabel: "21:02 UTC",
    ago: "1 d",
    operatorName: "Bharat C.",
    operatorEmail: "ops@roadbridge.app",
    isSystem: false,
    action: "impersonate.end",
    actionKind: "info",
    target: "user_01KK4F…BT44X",
    targetSub: "Bharat Chitnavis (self-impersonation test)",
    detail: "duration 4m 12s · session.expired auto",
    detailKind: "ok",
  },
];

export function getUserById(id: string): AdminUser | undefined {
  return ADMIN_USERS.find((u) => u.id === id);
}
